import type { AppState, EraId } from '../science/types';

export interface PresetDefinition {
  label: string;
  validation: 'baseline' | 'contextual' | 'benchmark' | 'qualified' | 'historical-reconstruction';
  note: string;
  sourceIds: string[];
  apply?: (draft: AppState) => void;
}

const setCommon = (draft: AppState): void => {
  draft.scientific.boundaryModulation = false;
  draft.scientific.modulationAmplitude = 0;
  draft.scientific.squeezingAngle = 45;
  draft.scientific.detectorEfficiency = 0.85;
  draft.scientific.bandShape = 'gaussian';
  draft.visualization.lightConesVisible = false;
};

const modern: PresetDefinition[] = [
  {
    label: '2019 Electro-Optic Vacuum Correlations',
    validation: 'contextual',
    note: 'Band-limited free-space baseline contextualized to the 2019 THz electro-optic correlation experiment. The ZnTe transfer function and finite probe volumes are not reconstructed.',
    sourceIds: ['benea-chelmus-2019'],
    apply: (draft) => {
      setCommon(draft);
      draft.scientific.frequencyLog10 = Math.log10(1.5e12);
      draft.scientific.detectorBandwidthLog10 = Math.log10(1.8e12);
      draft.scientific.temperature = 4;
      draft.scientific.state = 'vacuum';
      draft.scientific.fieldComponent = 'x';
      draft.scientific.probeSeparation = 0;
      draft.scientific.probeDelay = 0;
      draft.scientific.boundaryGeometry = 'none';
      draft.scientific.randomSeed = 21092019;
    }
  },
  {
    label: '2022 Spacetime-Separated Optical Probes',
    validation: 'contextual',
    note: 'Ideal transverse free-space correlation baseline with the reported 50 micrometre probe separation and 470 femtosecond timing scale. Crystal dispersion and pulse-volume convolution are deferred.',
    sourceIds: ['lindel-2022'],
    apply: (draft) => {
      setCommon(draft);
      draft.scientific.frequencyLog10 = Math.log10(1.5e12);
      draft.scientific.detectorBandwidthLog10 = Math.log10(1.8e12);
      draft.scientific.temperature = 4;
      draft.scientific.state = 'vacuum';
      draft.scientific.fieldComponent = 'x';
      draft.scientific.probeSeparation = 0.05;
      draft.scientific.probeDelay = 0.47;
      draft.scientific.boundaryGeometry = 'none';
      draft.visualization.lightConesVisible = true;
      draft.scientific.randomSeed = 13062022;
    }
  },
  {
    label: 'Direct Electric-Field Sampling',
    validation: 'contextual',
    note: 'Band-limited free-space field statistics associated with subcycle electro-optic sampling. The experimental response function remains outside the current model.',
    sourceIds: ['riek-2015'],
    apply: (draft) => {
      setCommon(draft);
      draft.scientific.frequencyLog10 = 12.5;
      draft.scientific.detectorBandwidthLog10 = 12;
      draft.scientific.temperature = 4;
      draft.scientific.state = 'vacuum';
      draft.scientific.probeSeparation = 0;
      draft.scientific.probeDelay = 0;
      draft.scientific.boundaryGeometry = 'none';
      draft.scientific.randomSeed = 23102015;
    }
  },
  {
    label: 'Squeezed Vacuum Correlation Study',
    validation: 'qualified',
    note: 'Free-space spectrum with a declared single covariance-envelope squeezed-state extension.',
    sourceIds: ['caves-1981', 'walls-1983'],
    apply: (draft) => {
      setCommon(draft);
      draft.scientific.frequencyLog10 = 10;
      draft.scientific.detectorBandwidthLog10 = 9;
      draft.scientific.temperature = 0.02;
      draft.scientific.state = 'squeezed';
      draft.scientific.squeezingDb = 6;
      draft.scientific.squeezingAngle = 37;
      draft.scientific.probeSeparation = 0;
      draft.scientific.probeDelay = 0;
      draft.scientific.randomSeed = 15112011;
    }
  },
  {
    label: 'Casimir Parallel Plates',
    validation: 'benchmark',
    note: 'Free-space field baseline plus the analytic ideal zero-temperature parallel-plate Casimir energy and pressure.',
    sourceIds: ['casimir-1948'],
    apply: (draft) => {
      setCommon(draft);
      draft.scientific.frequencyLog10 = 14;
      draft.scientific.detectorBandwidthLog10 = 12;
      draft.scientific.temperature = 0;
      draft.scientific.state = 'vacuum';
      draft.scientific.boundaryGeometry = 'plates';
      draft.scientific.boundarySeparationLog10 = -7;
      draft.scientific.randomSeed = 29051948;
    }
  }
];

const dce: PresetDefinition[] = [
  {
    label: '2011 Dynamical Casimir Circuit',
    validation: 'benchmark',
    note: 'Perturbative open-waveguide SQUID boundary model configured near the microwave regime of the 2011 superconducting-circuit experiment. The cryogenic receiver chain is not reconstructed.',
    sourceIds: ['johansson-2010', 'wilson-2011'],
    apply: (draft) => {
      setCommon(draft);
      draft.scientific.temperature = 0.05;
      draft.scientific.state = 'squeezed';
      draft.scientific.frequencyLog10 = Math.log10(5.4e9);
      draft.scientific.detectorBandwidthLog10 = Math.log10(5e9);
      draft.scientific.boundaryGeometry = 'waveguide';
      draft.scientific.boundaryModulation = true;
      draft.scientific.modulationFrequencyLog10 = Math.log10(10.8e9);
      draft.scientific.modulationAmplitude = 0.25;
      draft.scientific.effectiveLengthLog10 = Math.log10(0.44e-3);
      draft.scientific.phaseVelocityFraction = 0.4;
      draft.scientific.qualityFactor = 50;
      draft.scientific.squeezingDb = 3;
      draft.scientific.squeezingAngle = 45;
      draft.scientific.randomSeed = 15112011;
    }
  },
  {
    label: 'Johansson Perturbative Benchmark',
    validation: 'benchmark',
    note: 'Theory benchmark using the representative 18.6 GHz drive, 0.44 mm effective length, 0.11 mm modulation, and 1.2e8 m/s phase velocity used in Johansson et al.',
    sourceIds: ['johansson-2010'],
    apply: (draft) => {
      setCommon(draft);
      draft.scientific.temperature = 0.02;
      draft.scientific.state = 'squeezed';
      draft.scientific.frequencyLog10 = Math.log10(9.3e9);
      draft.scientific.detectorBandwidthLog10 = Math.log10(9e9);
      draft.scientific.boundaryGeometry = 'waveguide';
      draft.scientific.boundaryModulation = true;
      draft.scientific.modulationFrequencyLog10 = Math.log10(18.6e9);
      draft.scientific.modulationAmplitude = 0.25;
      draft.scientific.effectiveLengthLog10 = Math.log10(0.44e-3);
      draft.scientific.phaseVelocityFraction = 1.2e8 / 299792458;
      draft.scientific.qualityFactor = 100;
      draft.scientific.squeezingAngle = 45;
      draft.scientific.randomSeed = 20102010;
    }
  },
  {
    label: 'Superconducting Parametric Amplifier',
    validation: 'qualified',
    note: 'Weakly modulated transmission-line boundary shown as a parametric amplifier. Gain and noise are idealized through the same perturbative pair-production model.',
    sourceIds: ['johansson-2009', 'johansson-2010'],
    apply: (draft) => {
      setCommon(draft);
      draft.scientific.temperature = 0.035;
      draft.scientific.state = 'squeezed';
      draft.scientific.frequencyLog10 = Math.log10(6e9);
      draft.scientific.detectorBandwidthLog10 = Math.log10(4e9);
      draft.scientific.boundaryGeometry = 'waveguide';
      draft.scientific.boundaryModulation = true;
      draft.scientific.modulationFrequencyLog10 = Math.log10(12e9);
      draft.scientific.modulationAmplitude = 0.12;
      draft.scientific.effectiveLengthLog10 = Math.log10(0.6e-3);
      draft.scientific.phaseVelocityFraction = 0.38;
      draft.scientific.squeezingAngle = 45;
      draft.scientific.randomSeed = 11082012;
    }
  },
  {
    label: 'Two-Mode Microwave Squeezing',
    validation: 'qualified',
    note: 'Signal-idler frequencies are constrained to sum to the boundary drive frequency; the quadrature panel exposes the leading-order two-mode squeezing envelope.',
    sourceIds: ['johansson-2010', 'wilson-2011'],
    apply: (draft) => {
      setCommon(draft);
      draft.scientific.temperature = 0.02;
      draft.scientific.state = 'squeezed';
      draft.scientific.frequencyLog10 = Math.log10(4.8e9);
      draft.scientific.detectorBandwidthLog10 = Math.log10(3e9);
      draft.scientific.boundaryGeometry = 'waveguide';
      draft.scientific.boundaryModulation = true;
      draft.scientific.modulationFrequencyLog10 = Math.log10(11e9);
      draft.scientific.modulationAmplitude = 0.18;
      draft.scientific.effectiveLengthLog10 = Math.log10(0.5e-3);
      draft.scientific.phaseVelocityFraction = 0.4;
      draft.scientific.squeezingAngle = 45;
      draft.scientific.randomSeed = 12062011;
    }
  },
  {
    label: 'Circuit QED Boundary Modulation',
    validation: 'historical-reconstruction',
    note: 'Interactive weak-modulation circuit-QED teaching preset. It preserves the input-output equations while leaving device-specific SQUID inductance and plasma-frequency calibration outside scope.',
    sourceIds: ['johansson-2009', 'johansson-2010'],
    apply: (draft) => {
      setCommon(draft);
      draft.scientific.temperature = 0.08;
      draft.scientific.state = 'thermal';
      draft.scientific.frequencyLog10 = Math.log10(3.5e9);
      draft.scientific.detectorBandwidthLog10 = Math.log10(2e9);
      draft.scientific.boundaryGeometry = 'waveguide';
      draft.scientific.boundaryModulation = true;
      draft.scientific.modulationFrequencyLog10 = Math.log10(8e9);
      draft.scientific.modulationAmplitude = 0.08;
      draft.scientific.effectiveLengthLog10 = Math.log10(0.35e-3);
      draft.scientific.phaseVelocityFraction = 0.35;
      draft.scientific.randomSeed = 15012010;
    }
  }
];

const homodyne: PresetDefinition[] = [
  {
    label: 'Squeezed Vacuum Homodyne Laboratory',
    validation: 'qualified',
    note: 'Ideal single-mode squeezed vacuum with a Lorentzian cavity and balanced-homodyne sampling.',
    sourceIds: ['caves-1981', 'walls-1983', 'schumaker-caves-1985'],
    apply: (draft) => {
      setCommon(draft);
      draft.scientific.frequencyLog10 = Math.log10(2.818e14);
      draft.scientific.detectorBandwidthLog10 = 7;
      draft.scientific.temperature = 0;
      draft.scientific.state = 'squeezed';
      draft.scientific.squeezingDb = 6;
      draft.scientific.squeezingAngle = 37;
      draft.scientific.qualityFactor = 2e6;
      draft.scientific.boundaryGeometry = 'cavity';
      draft.scientific.randomSeed = 16061998;
    }
  },
  {
    label: 'Optical Cavity Mode Suppression',
    validation: 'qualified',
    note: 'Lorentzian cavity mode with phase-selected squeezed and anti-squeezed noise spectra.',
    sourceIds: ['walls-1983'],
    apply: (draft) => {
      setCommon(draft);
      draft.scientific.frequencyLog10 = Math.log10(2.818e14);
      draft.scientific.detectorBandwidthLog10 = 6.5;
      draft.scientific.temperature = 0;
      draft.scientific.state = 'squeezed';
      draft.scientific.squeezingDb = 4.5;
      draft.scientific.squeezingAngle = 0;
      draft.scientific.qualityFactor = 5e6;
      draft.scientific.boundaryGeometry = 'cavity';
      draft.scientific.randomSeed = 19831983;
    }
  },
  {
    label: 'Balanced Homodyne Quadratures',
    validation: 'qualified',
    note: 'Rotates the local-oscillator phase through the covariance ellipse and reports the corresponding ideal quadrature variance.',
    sourceIds: ['caves-1981', 'schumaker-caves-1985'],
    apply: (draft) => {
      setCommon(draft);
      draft.scientific.frequencyLog10 = Math.log10(2.82e14);
      draft.scientific.detectorBandwidthLog10 = 8;
      draft.scientific.temperature = 0;
      draft.scientific.state = 'squeezed';
      draft.scientific.squeezingDb = 8;
      draft.scientific.squeezingAngle = 22.5;
      draft.scientific.qualityFactor = 1e6;
      draft.scientific.boundaryGeometry = 'none';
      draft.scientific.randomSeed = 25031985;
    }
  },
  {
    label: 'Thermal-to-Squeezed Transition',
    validation: 'qualified',
    note: 'Shows how finite thermal occupation inflates both quadrature variances before squeezing redistributes the noise.',
    sourceIds: ['caves-1981', 'walls-1983'],
    apply: (draft) => {
      setCommon(draft);
      draft.scientific.frequencyLog10 = Math.log10(8e9);
      draft.scientific.detectorBandwidthLog10 = 7;
      draft.scientific.temperature = 0.12;
      draft.scientific.state = 'squeezed';
      draft.scientific.squeezingDb = 6;
      draft.scientific.squeezingAngle = 45;
      draft.scientific.qualityFactor = 2e4;
      draft.scientific.boundaryGeometry = 'cavity';
      draft.scientific.randomSeed = 12121999;
    }
  },
  {
    label: 'Parametric Down-Conversion Teaching Model',
    validation: 'historical-reconstruction',
    note: 'Gaussian output-state teaching preset. Pump depletion, multimode phase matching, and nonlinear-crystal dispersion are not included.',
    sourceIds: ['caves-1981', 'schumaker-caves-1985'],
    apply: (draft) => {
      setCommon(draft);
      draft.scientific.frequencyLog10 = Math.log10(2.82e14);
      draft.scientific.detectorBandwidthLog10 = 9;
      draft.scientific.temperature = 0;
      draft.scientific.state = 'squeezed';
      draft.scientific.squeezingDb = 10;
      draft.scientific.squeezingAngle = 45;
      draft.scientific.qualityFactor = 5e5;
      draft.scientific.boundaryGeometry = 'custom';
      draft.scientific.randomSeed = 20012000;
    }
  }
];

const dynamic: PresetDefinition[] = [
  {
    label: 'Moving-Mirror Parametric Model',
    validation: 'historical-reconstruction',
    note: 'Single-mode rotating-wave approximation to resonant boundary modulation. Apparent mirror displacement is visually magnified.',
    sourceIds: ['moore-1970', 'law-1994'],
    apply: (draft) => {
      setCommon(draft);
      draft.scientific.cavityResonanceLog10 = Math.log10(5e9);
      draft.scientific.frequencyLog10 = Math.log10(5e9);
      draft.scientific.modulationFrequencyLog10 = Math.log10(10e9);
      draft.scientific.modulationAmplitude = 0.002;
      draft.scientific.integrationTimeLog10 = -8;
      draft.scientific.qualityFactor = 1e5;
      draft.scientific.state = 'squeezed';
      draft.scientific.boundaryGeometry = 'plates';
      draft.scientific.boundaryModulation = true;
      draft.scientific.randomSeed = 1091970;
    }
  },
  {
    label: 'Variable-Length One-Dimensional Cavity',
    validation: 'historical-reconstruction',
    note: 'One-dimensional cavity mode under weak sinusoidal length modulation, presented through the effective parametric Hamiltonian rather than Moore’s exact trajectory equation.',
    sourceIds: ['moore-1970', 'law-1994'],
    apply: (draft) => {
      setCommon(draft);
      draft.scientific.cavityResonanceLog10 = Math.log10(3e9);
      draft.scientific.frequencyLog10 = Math.log10(3e9);
      draft.scientific.modulationFrequencyLog10 = Math.log10(6e9);
      draft.scientific.modulationAmplitude = 0.001;
      draft.scientific.integrationTimeLog10 = -8.5;
      draft.scientific.qualityFactor = 5e4;
      draft.scientific.state = 'vacuum';
      draft.scientific.boundaryGeometry = 'cavity';
      draft.scientific.boundaryModulation = true;
      draft.scientific.randomSeed = 11091970;
    }
  },
  {
    label: 'Time-Dependent Boundary Modes',
    validation: 'historical-reconstruction',
    note: 'Detunes the boundary drive from twice the cavity resonance to expose the finite parametric gain window.',
    sourceIds: ['moore-1970', 'law-1994'],
    apply: (draft) => {
      setCommon(draft);
      draft.scientific.cavityResonanceLog10 = Math.log10(4e9);
      draft.scientific.frequencyLog10 = Math.log10(4e9);
      draft.scientific.modulationFrequencyLog10 = Math.log10(7.85e9);
      draft.scientific.modulationAmplitude = 0.004;
      draft.scientific.integrationTimeLog10 = -8.2;
      draft.scientific.qualityFactor = 2e5;
      draft.scientific.state = 'vacuum';
      draft.scientific.boundaryGeometry = 'cavity';
      draft.scientific.boundaryModulation = true;
      draft.scientific.randomSeed = 19751975;
    }
  },
  {
    label: 'Parametric Amplification',
    validation: 'qualified',
    note: 'Near-threshold single-mode amplification with phenomenological cavity loss. Values above threshold are capped before numerical overflow and reported as such.',
    sourceIds: ['law-1994'],
    apply: (draft) => {
      setCommon(draft);
      draft.scientific.cavityResonanceLog10 = Math.log10(6e9);
      draft.scientific.frequencyLog10 = Math.log10(6e9);
      draft.scientific.modulationFrequencyLog10 = Math.log10(12e9);
      draft.scientific.modulationAmplitude = 0.01;
      draft.scientific.integrationTimeLog10 = -8.7;
      draft.scientific.qualityFactor = 2e3;
      draft.scientific.state = 'squeezed';
      draft.scientific.boundaryGeometry = 'waveguide';
      draft.scientific.boundaryModulation = true;
      draft.scientific.randomSeed = 19801980;
    }
  },
  {
    label: 'Accelerating Boundary Teaching Model',
    validation: 'historical-reconstruction',
    note: 'Pedagogical boundary-acceleration view using the same single-mode parametric approximation. It is not an Unruh detector calculation.',
    sourceIds: ['moore-1970', 'law-1994'],
    apply: (draft) => {
      setCommon(draft);
      draft.scientific.cavityResonanceLog10 = Math.log10(2e9);
      draft.scientific.frequencyLog10 = Math.log10(2e9);
      draft.scientific.modulationFrequencyLog10 = Math.log10(4e9);
      draft.scientific.modulationAmplitude = 0.006;
      draft.scientific.integrationTimeLog10 = -8;
      draft.scientific.qualityFactor = 8e4;
      draft.scientific.state = 'vacuum';
      draft.scientific.boundaryGeometry = 'custom';
      draft.scientific.boundaryModulation = true;
      draft.scientific.randomSeed = 17071979;
    }
  }
];

const staticCasimir: PresetDefinition[] = [
  {
    label: 'Casimir Parallel Plates',
    validation: 'benchmark',
    note: 'Ideal perfectly conducting, zero-temperature parallel plates with analytic energy per area and pressure.',
    sourceIds: ['casimir-1948'],
    apply: (draft) => {
      setCommon(draft);
      draft.scientific.frequencyLog10 = 15;
      draft.scientific.detectorBandwidthLog10 = 9;
      draft.scientific.temperature = 0;
      draft.scientific.state = 'vacuum';
      draft.scientific.boundaryGeometry = 'plates';
      draft.scientific.boundarySeparationLog10 = -7;
      draft.scientific.spatialLog10 = -6;
      draft.scientific.randomSeed = 29051948;
    }
  },
  {
    label: 'Sphere–Plane Boundary Geometry',
    validation: 'qualified',
    note: 'Ideal sphere-plane proximity-force approximation. Validity requires separation much smaller than the selected sphere radius.',
    sourceIds: ['casimir-1948', 'bordag-2009'],
    apply: (draft) => {
      setCommon(draft);
      draft.scientific.frequencyLog10 = 15;
      draft.scientific.temperature = 0;
      draft.scientific.state = 'vacuum';
      draft.scientific.boundaryGeometry = 'sphere';
      draft.scientific.boundarySeparationLog10 = Math.log10(120e-9);
      draft.scientific.spatialLog10 = Math.log10(100e-6);
      draft.scientific.randomSeed = 19581958;
    }
  },
  {
    label: 'Cavity Mode Density',
    validation: 'historical-reconstruction',
    note: 'Finite cutoff visualization of discrete normal modes. The analytic renormalized plate force remains separate from the displayed finite mode sum.',
    sourceIds: ['casimir-1948', 'milonni-1994'],
    apply: (draft) => {
      setCommon(draft);
      draft.scientific.frequencyLog10 = Math.log10(8e15);
      draft.scientific.temperature = 0;
      draft.scientific.state = 'vacuum';
      draft.scientific.boundaryGeometry = 'cavity';
      draft.scientific.boundarySeparationLog10 = Math.log10(250e-9);
      draft.scientific.numericalResolution = 96;
      draft.scientific.randomSeed = 1011960;
    }
  },
  {
    label: 'Atom–Surface Interaction',
    validation: 'qualified',
    note: 'Retarded perfect-conductor Casimir–Polder asymptote using an isotropic static polarizability volume.',
    sourceIds: ['casimir-polder-1948'],
    apply: (draft) => {
      setCommon(draft);
      draft.scientific.frequencyLog10 = 15;
      draft.scientific.temperature = 0;
      draft.scientific.state = 'vacuum';
      draft.scientific.boundaryGeometry = 'custom';
      draft.scientific.boundarySeparationLog10 = Math.log10(500e-9);
      draft.scientific.polarizabilityVolumeLog10 = Math.log10(0.667e-30);
      draft.scientific.randomSeed = 19481948;
    }
  },
  {
    label: 'Waveguide Boundary Modes',
    validation: 'historical-reconstruction',
    note: 'Discrete-mode teaching view in a static waveguide-like boundary. Material dispersion and complete waveguide Green functions are not included.',
    sourceIds: ['milonni-1994'],
    apply: (draft) => {
      setCommon(draft);
      draft.scientific.frequencyLog10 = 12;
      draft.scientific.temperature = 0;
      draft.scientific.state = 'vacuum';
      draft.scientific.boundaryGeometry = 'waveguide';
      draft.scientific.boundarySeparationLog10 = Math.log10(0.5e-3);
      draft.scientific.randomSeed = 19651965;
    }
  }
];

const foundations: PresetDefinition[] = [
  {
    label: 'Lamb–Retherford Spectroscopy',
    validation: 'historical-reconstruction',
    note: 'Measured 2S1/2–2P1/2 separation retained as an empirical benchmark, paired with an explicitly simplified leading-log teaching estimate.',
    sourceIds: ['lamb-retherford-1947', 'bethe-1947'],
    apply: (draft) => {
      setCommon(draft);
      draft.scientific.frequencyLog10 = Math.log10(1057.8e6);
      draft.scientific.detectorBandwidthLog10 = Math.log10(20e6);
      draft.scientific.temperature = 4;
      draft.scientific.state = 'vacuum';
      draft.scientific.atomicNumber = 1;
      draft.scientific.cutoffRatio = 2436.6195992402063;
      draft.scientific.boundaryGeometry = 'none';
      draft.scientific.randomSeed = 2081947;
    }
  },
  {
    label: 'Hydrogen 2S–2P Energy Shift',
    validation: 'historical-reconstruction',
    note: 'Hydrogenic leading-log sensitivity study with the historical observed shift shown as the fixed comparison line.',
    sourceIds: ['lamb-retherford-1947', 'bethe-1947', 'welton-1948'],
    apply: (draft) => {
      setCommon(draft);
      draft.scientific.frequencyLog10 = Math.log10(1057.8e6);
      draft.scientific.detectorBandwidthLog10 = Math.log10(12e6);
      draft.scientific.temperature = 0;
      draft.scientific.state = 'vacuum';
      draft.scientific.atomicNumber = 1;
      draft.scientific.cutoffRatio = 2436.6195992402063;
      draft.scientific.boundaryGeometry = 'none';
      draft.scientific.randomSeed = 19471947;
    }
  },
  {
    label: 'Early QED Self-Energy Calculation',
    validation: 'historical-reconstruction',
    note: 'Exposes the logarithmic cutoff dependence of a simplified Bethe-style hydrogenic leading-log estimate. It is not a modern bound-state QED result.',
    sourceIds: ['bethe-1947'],
    apply: (draft) => {
      setCommon(draft);
      draft.scientific.frequencyLog10 = Math.log10(1057.8e6);
      draft.scientific.detectorBandwidthLog10 = Math.log10(30e6);
      draft.scientific.temperature = 0;
      draft.scientific.state = 'vacuum';
      draft.scientific.atomicNumber = 1;
      draft.scientific.cutoffRatio = 137;
      draft.scientific.boundaryGeometry = 'custom';
      draft.scientific.randomSeed = 30111947;
    }
  },
  {
    label: 'Microwave Spectrometer Apparatus',
    validation: 'historical-reconstruction',
    note: 'Spectrometer-centered teaching preset with a broader line width and reproducible count sampling.',
    sourceIds: ['lamb-retherford-1947'],
    apply: (draft) => {
      setCommon(draft);
      draft.scientific.frequencyLog10 = Math.log10(1057.8e6);
      draft.scientific.detectorBandwidthLog10 = Math.log10(80e6);
      draft.scientific.temperature = 77;
      draft.scientific.state = 'thermal';
      draft.scientific.atomicNumber = 1;
      draft.scientific.cutoffRatio = 2436.6195992402063;
      draft.scientific.boundaryGeometry = 'none';
      draft.scientific.randomSeed = 1011948;
    }
  },
  {
    label: 'Atomic-Level Shift Teaching Model',
    validation: 'historical-reconstruction',
    note: 'Hydrogenic Z-scaling demonstration. Higher-Z values are pedagogical because omitted relativistic and nuclear corrections rapidly become important.',
    sourceIds: ['bethe-1947', 'welton-1948'],
    apply: (draft) => {
      setCommon(draft);
      draft.scientific.frequencyLog10 = Math.log10(1057.8e6);
      draft.scientific.detectorBandwidthLog10 = Math.log10(20e6);
      draft.scientific.temperature = 0;
      draft.scientific.state = 'vacuum';
      draft.scientific.atomicNumber = 2;
      draft.scientific.cutoffRatio = 2436.6195992402063;
      draft.scientific.boundaryGeometry = 'custom';
      draft.scientific.randomSeed = 19481949;
    }
  }
];

export const PRESET_DEFINITIONS: Record<EraId, PresetDefinition[]> = {
  modern,
  '2010s': dce,
  '1990s-2000s': homodyne,
  '1970s-1980s': dynamic,
  '1950s-1960s': staticCasimir,
  '1940s': foundations
};

export const getPresetDefinition = (era: EraId, label: string): PresetDefinition | undefined => PRESET_DEFINITIONS[era].find((preset) => preset.label === label);

export const applyPresetDefinition = (draft: AppState, label: string): void => {
  draft.preset = label;
  getPresetDefinition(draft.era, label)?.apply?.(draft);
};
